/**
 * Tenant brand → full Material 3 color scheme generator.
 *
 * Given a tenant's brand primary (and optional secondary) hex, derives a
 * complete M3 tonal scheme. Theme composition consumes its expressive accent
 * roles while preserving neutral app chrome. The returned object mirrors the
 * data shape of the static palettes in palettes.js, so cssTokens.js and the
 * MUI theme can compose it identically. Returns null when no valid brand
 * primary is supplied.
 */

import {
  argbFromHex,
  hexFromArgb,
  Hct,
  MaterialDynamicColors,
  SchemeTonalSpot,
  TonalPalette,
} from '@material/material-color-utilities';

const MDC = MaterialDynamicColors;
const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

// palettes.js key → MaterialDynamicColors role. Keys mirror
// LIGHT_PALETTE/DARK_PALETTE exactly (minus primaryRgb, derived below).
const ROLE_BY_KEY = {
  primary: MDC.primary,
  onPrimary: MDC.onPrimary,
  primaryContainer: MDC.primaryContainer,
  onPrimaryContainer: MDC.onPrimaryContainer,
  secondary: MDC.secondary,
  onSecondary: MDC.onSecondary,
  secondaryContainer: MDC.secondaryContainer,
  onSecondaryContainer: MDC.onSecondaryContainer,
  tertiary: MDC.tertiary,
  onTertiary: MDC.onTertiary,
  tertiaryContainer: MDC.tertiaryContainer,
  onTertiaryContainer: MDC.onTertiaryContainer,
  error: MDC.error,
  onError: MDC.onError,
  errorContainer: MDC.errorContainer,
  onErrorContainer: MDC.onErrorContainer,
  background: MDC.background,
  onBackground: MDC.onBackground,
  surface: MDC.surface,
  onSurface: MDC.onSurface,
  surfaceVariant: MDC.surfaceVariant,
  onSurfaceVariant: MDC.onSurfaceVariant,
  outline: MDC.outline,
  outlineVariant: MDC.outlineVariant,
  shadow: MDC.shadow,
  scrim: MDC.scrim,
  inverseSurface: MDC.inverseSurface,
  inverseOnSurface: MDC.inverseOnSurface,
  inversePrimary: MDC.inversePrimary,
  surfaceDim: MDC.surfaceDim,
  surfaceBright: MDC.surfaceBright,
  surfaceContainerLowest: MDC.surfaceContainerLowest,
  surfaceContainerLow: MDC.surfaceContainerLow,
  surfaceContainer: MDC.surfaceContainer,
  surfaceContainerHigh: MDC.surfaceContainerHigh,
  surfaceContainerHighest: MDC.surfaceContainerHighest,
};

// Standard M3 tones for an explicitly-supplied secondary swatch, so a
// tenant's BRAND_SECONDARY is honored rather than M3's auto-derived one.
const SECONDARY_TONES = {
  light: { secondary: 40, onSecondary: 100, secondaryContainer: 90, onSecondaryContainer: 10 },
  dark: { secondary: 80, onSecondary: 20, secondaryContainer: 30, onSecondaryContainer: 90 },
};

const rgbStringFromArgb = argb => `${(argb >> 16) & 0xff} ${(argb >> 8) & 0xff} ${argb & 0xff}`;

/**
 * @param {string} primaryHex  tenant brand primary (e.g. '#74a84a'); generates tonal accent roles
 * @param {string|null} secondaryHex  optional valid secondary; overrides the M3-derived one
 * @param {'light'|'dark'} scheme
 * @returns {object|null} palette matching palettes.js shape, or null when primary is absent/invalid
 */
export const generateBrandPalette = (primaryHex, secondaryHex, scheme) => {
  if (!primaryHex || !HEX_COLOR.test(primaryHex)) return null;
  try {
    const isDark = scheme === 'dark';
    const dynamicScheme = new SchemeTonalSpot(Hct.fromInt(argbFromHex(primaryHex)), isDark, 0);

    const palette = {};
    Object.entries(ROLE_BY_KEY).forEach(([key, role]) => {
      palette[key] = hexFromArgb(role.getArgb(dynamicScheme));
    });
    palette.primaryRgb = rgbStringFromArgb(MDC.primary.getArgb(dynamicScheme));

    if (secondaryHex && HEX_COLOR.test(secondaryHex)) {
      const sec = TonalPalette.fromInt(argbFromHex(secondaryHex));
      const tones = SECONDARY_TONES[isDark ? 'dark' : 'light'];
      palette.secondary = hexFromArgb(sec.tone(tones.secondary));
      palette.onSecondary = hexFromArgb(sec.tone(tones.onSecondary));
      palette.secondaryContainer = hexFromArgb(sec.tone(tones.secondaryContainer));
      palette.onSecondaryContainer = hexFromArgb(sec.tone(tones.onSecondaryContainer));
    }

    return palette;
  } catch {
    // Bad hex / library failure — fall back to the static default palette.
    return null;
  }
};
